import { Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { formatDate } from "@entrilia/shared/utilities/dateUtils";
import { formatNumber } from "@entrilia/shared/utilities/formatters";
import adminApi from "../../../../../api/adminApi";
import type { Category } from "../../../../../api/types/accessTypes";
import type { InvestorFund, SearchInvestorDocumentsRequest } from "../../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../../context/useClientContext";
import { pageRoutes } from "../../../../../routes";
import { getReferrerLocationState } from "../../../../../utilities/navigationHelper";
import FundStructureDocumentFilters from "../../common/FundStructureDocumentFilters";
import {
  clearFiltersAction,
  getInitialFundStructureDocumentsFilterState,
  getSearchDocumentsFilterPayload,
} from "../../common/fundStructureDocumentsFilters";
import FundStructureDocumentsGrid from "../../common/FundStructureDocumentsGrid";

interface Props {
  investorId: string;
  investorName: string;
  categories: Category[];
  investorFunds: InvestorFund[];
}

const exportInvestorDocument = withErrorHandling(adminApi.exportInvestorDocumentsToExcel);
const getInvestorDocumentsDownloadToken = withErrorHandling(adminApi.getInvestorDocumentsDownloadToken);

const InvestorDetailsDocumentsTab = ({ investorId, investorName, categories, investorFunds }: Props) => {
  const { clientCode, hasPermissions } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [filterState, setFilterState] = useState(getInitialFundStructureDocumentsFilterState);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const filterPayload = useMemo(() => getSearchDocumentsFilterPayload(filterState), [filterState]);

  const getInvestorDocuments = useCallback(
    (req: SearchInvestorDocumentsRequest) => adminApi.searchInvestorDocuments(investorId, req),
    [investorId]
  );

  const { gridApiRef, dataSource, totalRows, isLoading, refreshDataSource } = useGridDataSourceWithRange(
    getInvestorDocuments,
    filterPayload
  );

  const categoryOptions = useMemo(
    () =>
      categories
        .filter((c) => c.type === "Message")
        .map(({ externalId, name }) => ({ value: externalId, label: name })),
    [categories]
  );

  const fundOptions = useMemo(
    () =>
      investorFunds.map(({ fundId, fundName }) => ({
        value: fundId,
        label: fundName,
      })),
    [investorFunds]
  );

  const handleExportToExcel = async (withFilters: boolean) => {
    const payload: SearchInvestorDocumentsRequest = withFilters
      ? { range: {}, ...filterPayload }
      : { range: {}, publishingStatusFilter: "Any" };

    const fileName = `${investorName}_documents_${formatDate(new Date(), "yyyyMMdd_HHmmss")}.xlsx`;

    setIsExporting(true);
    const [resp, error] = await exportInvestorDocument(investorId, payload, fileName);
    setIsExporting(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[InvestorDetailsDocumentsTab] exportInvestorDocumentsToExcel");
      return;
    }

    await downloadFileFromUrl(resp.downloadUrl);

    if (resp.isTruncated) {
      sendNotification(
        `Exported first ${formatNumber(resp.exportedRecordsCount)} records. Please use filters to reduce the dataset and export additional records.`
      );
    }
  };

  const handleDownloadDocuments = async (withFilters: boolean) => {
    const payload: SearchInvestorDocumentsRequest = withFilters
      ? { range: {}, ...filterPayload }
      : { range: {}, publishingStatusFilter: "Any" };

    setIsDownloading(true);
    const [download, error] = await getInvestorDocumentsDownloadToken(investorId, payload);
    setIsDownloading(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[InvestorDetailsDocumentsTab] getInvestorDocumentsDownloadToken");
      return;
    }

    await downloadFileFromUrl(adminApi.createArchiveDownloadUrl(clientCode, download.token));

    if (download.isTruncated) {
      sendNotification(
        `Downloaded first ${formatNumber(download.documentCount)} documents. Please use filters to reduce the dataset and download additional documents.`
      );
    }
  };

  const handleClearFilters = () => {
    setFilterState(clearFiltersAction());
  };

  const handleNavigateToDocumentActivity = (postMessageRequestId: string, attachmentId: string) => {
    navigate(
      `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.investors}/${investorId}/${pageRoutes.messages}/${postMessageRequestId}/${pageRoutes.documents}/${attachmentId}/${pageRoutes.activity}`,
      { state: getReferrerLocationState(location) }
    );
  };

  const canDownloadDocuments = hasPermissions(["ViewDocumentCollections"]);

  return (
    <Stack spacing={2.5} sx={{ height: "100%" }}>
      <FundStructureDocumentFilters
        categoryOptions={categoryOptions}
        fundOptions={fundOptions}
        filterState={filterState}
        onChange={setFilterState}
        onClear={handleClearFilters}
        isLoading={isLoading}
        recordsCount={totalRows ?? 0}
        onExportAllRecords={() => handleExportToExcel(false)}
        onExportFilteredRecords={() => handleExportToExcel(true)}
        isExporting={isExporting}
        onDownloadAllDocuments={canDownloadDocuments ? () => handleDownloadDocuments(false) : undefined}
        onDownloadFilteredDocuments={canDownloadDocuments ? () => handleDownloadDocuments(true) : undefined}
        isDownloading={isDownloading}
      />
      <FundStructureDocumentsGrid
        dataSource={dataSource}
        categories={categories}
        excludeColumns={["investorNames"]}
        onNavigateToDocumentActivity={handleNavigateToDocumentActivity}
        lazyLoading
        onRefreshDocuments={refreshDataSource}
        apiRef={gridApiRef}
        allowStatusChanges={hasPermissions(["ManageDocumentCollections"])}
      />
    </Stack>
  );
};

export default InvestorDetailsDocumentsTab;
